// Composer'da gönderilmeyi bekleyen ekler — Claude Desktop tarzı önizleme.
//
// Önceden yalnız "ataç + dosya adı" yazıyordu; yanlış dosyayı eklediğini
// göndermeden anlamak mümkün değildi. Artık resimler küçük resim olarak,
// belgeler tür rengiyle görünüyor.

import { useEffect, useState } from 'react'
import {
  X, FileText, FileSpreadsheet, FileType2, FileArchive, FileCode2, File as FileIcon,
} from 'lucide-react'

const fmtSize = (b) => {
  if (!b && b !== 0) return ''
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`
  if (b >= 1024) return `${Math.round(b / 1024)} KB`
  return `${b} B`
}

const isImage = (f) =>
  (f.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|avif)$/i.test(f.name || '')

/** Uzantı/MIME → ikon + renk. Word mavi, Excel yeşil, PDF kırmızı … */
function fileLook(f) {
  const n = (f.name || '').toLowerCase()
  const t = f.type || ''
  if (/\.(docx?|odt|rtf)$/.test(n) || t.includes('word'))
    return { Icon: FileType2, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Word' }
  if (/\.(xlsx?|xlsm|csv|ods)$/.test(n) || t.includes('sheet') || t.includes('excel'))
    return { Icon: FileSpreadsheet, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Excel' }
  if (/\.pdf$/.test(n) || t.includes('pdf'))
    return { Icon: FileText, color: 'text-red-600', bg: 'bg-red-50', label: 'PDF' }
  if (/\.(pptx?|odp)$/.test(n) || t.includes('presentation'))
    return { Icon: FileText, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Sunum' }
  if (/\.(zip|rar|7z|tar|gz)$/.test(n))
    return { Icon: FileArchive, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Arşiv' }
  if (/\.(json|xml|ya?ml|js|ts|py|sql|html?|css|md)$/.test(n))
    return { Icon: FileCode2, color: 'text-violet-600', bg: 'bg-violet-50', label: 'Kod' }
  if (/\.txt$/.test(n) || t.startsWith('text/'))
    return { Icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50', label: 'Metin' }
  return { Icon: FileIcon, color: 'text-gray-500', bg: 'bg-gray-50', label: '' }
}

/** Resim küçük resmi — objectURL sızdırmadan. */
function ImageThumb({ file }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)   // bileşen kalkınca bellek bırakılsın
  }, [file])
  if (!url) return <div className="w-full h-full bg-gray-100 animate-pulse" />
  return <img src={url} alt={file.name} className="w-full h-full object-cover" />
}

export default function ComposerAttachments({ files, onRemove }) {
  if (!files?.length) return null

  return (
    <div className="flex flex-wrap gap-2 px-2 pt-2">
      {files.map((f, i) => {
        const img = isImage(f)
        const { Icon, color, bg, label } = fileLook(f)
        return (
          <div
            key={`${f.name}-${f.size}-${i}`}
            title={`${f.name}${f.size ? ` · ${fmtSize(f.size)}` : ''}`}
            className="group relative flex items-center gap-2 rounded-xl border border-gray-200 bg-white
                       hover:border-gray-300 transition-colors overflow-hidden"
          >
            {img ? (
              // Küçük resim: göndermeden önce doğru görseli seçtiğini gör.
              <div className="w-[52px] h-[52px] shrink-0 bg-gray-50">
                <ImageThumb file={f} />
              </div>
            ) : (
              <div className={`w-[52px] h-[52px] shrink-0 flex items-center justify-center ${bg}`}>
                <Icon size={20} className={color} />
              </div>
            )}

            <div className="pr-7 py-1.5 min-w-0 max-w-[190px]">
              <div className="text-[12px] text-gray-800 truncate leading-tight">{f.name}</div>
              <div className="text-[10px] text-gray-400 leading-tight">
                {label && <span className="mr-1">{label}</span>}
                {fmtSize(f.size)}
              </div>
            </div>

            <button
              onClick={() => onRemove(i)}
              title="Kaldır"
              className="absolute top-1 right-1 p-0.5 rounded-md bg-white/90 text-gray-400
                         hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100
                         focus:opacity-100 transition-opacity"
            >
              <X size={13} />
            </button>
          </div>
        )
      })}
    </div>
  )
}
